import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FaCrown, FaExternalLinkAlt, FaSpinner, FaCheck } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import SubscriptionAlert from './SubscriptionAlert';
import SubscriptionDetails from './SubscriptionDetails';
import { useSubscription } from '../../hooks/useSubscription';
import LoadingSpinner from '../common/LoadingSpinner';
import subscriptionConfig from '../../config/subscription';

/**
 * Dashboard subscription management page
 * Displays current subscription details, status, and management options
 */
const SubscriptionPage = ({ userType = 'parent' }) => {
  const { loading, subscription, getCurrentTier, isSubscriptionActive, getDaysRemaining } = useSubscription();
  
  // Get subscription plan display name
  const getTierDisplayName = () => {
    const currentTier = getCurrentTier() || 'free';
    const tierNames = {
      'free': 'Free',
      'essential': 'Essential',
      'growth': 'Growth',
      'plus': 'Plus',
      'ignite': 'Ignite',
      'elevate': 'Elevate'
    };
    return tierNames[currentTier] || currentTier;
  };
  
  // Get FAQ items for the current user type
  const faqItems = subscriptionConfig.subscriptionFAQ.slice(0, 5); // Limit to 5 items
  
  return (
    <>
      <Helmet>
        <title>Subscription Management | Meira</title>
      </Helmet>
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <FaCrown className="mr-2 text-primary" /> Subscription Management
            </h1>
            <p className="text-gray-600 mt-1">
              View and manage your subscription details
            </p>
          </div>
          
          {/* Link to public subscription page */}
          <div className="mt-4 md:mt-0">
            <Link 
              to="/subscriptions" 
              className="inline-flex items-center text-primary hover:text-primary-dark transition-colors"
            >
              <span>View All Plans</span>
              <FaExternalLinkAlt className="ml-1 text-xs" />
            </Link>
          </div>
        </div>
        
        {/* Current Plan Banner */}
        {!loading && subscription && (
          <div className="bg-white rounded-lg shadow-md p-4 mb-6 border-l-4 border-primary">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div className="flex items-center mb-3 md:mb-0">
                <div className="bg-primary bg-opacity-10 rounded-full p-3 mr-4">
                  <FaCrown className="text-xl text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold">Current Plan: {getTierDisplayName()}</h2>
                  <p className="text-gray-600">
                    {subscription.billingCycle === 'annual' ? 'Annual' : 'Quarterly'} Billing
                    {isSubscriptionActive() && ` · ${getDaysRemaining()} days remaining`}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center mr-4 ${
                  isSubscriptionActive() ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {isSubscriptionActive() ? (
                    <>
                      <FaCheck className="mr-1" /> Active
                    </>
                  ) : 'Inactive'}
                </div>
                <Link 
                  to="/subscriptions" 
                  className="text-sm px-4 py-2 bg-primary text-white rounded hover:bg-primary-dark transition-colors"
                >
                  {getCurrentTier() === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                </Link>
              </div>
            </div>
          </div>
        )}
        
        {/* Subscription alert component */}
        <SubscriptionAlert />
        
        {/* Main content */}
        <div className="grid grid-cols-1 gap-6">
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-6 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {/* Subscription details card */}
              <SubscriptionDetails />
              
              {/* Subscription management guides */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-semibold mb-4">Subscription FAQs</h2>
                
                <div className="space-y-4">
                  {faqItems.map((faq, index) => (
                    <div key={index}>
                      <h3 className="font-medium text-primary">{faq.question}</h3>
                      <p className="text-gray-600 mt-1">{faq.answer}</p>
                    </div>
                  ))}
                  
                  {/* Support contact */}
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <h3 className="font-medium">Need more help?</h3>
                    <p className="text-gray-600 mt-1">
                      Contact our support team at <a href="mailto:support@meira.co.ke" className="text-primary hover:underline">support@meira.co.ke</a> or visit our <Link to="/help" className="text-primary hover:underline">help center</Link>.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriptionPage; 